import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { promiseWithCatch, unknownLocation } from 'src/app/common/app.helpers';
import ProjectLinks from 'src/app/common/project.links';
import { DefinedUserType, definedUserTypes } from 'src/app/components/profile/profile.enum';
import { ProfileFormTypes } from 'src/app/forms/forms.enum';
import { FormConstructorContent } from 'src/app/interfaces/form-and-field.interface';
import { RoleInfo } from 'src/app/models/role-info.model';
import { NotificationService } from 'src/app/notifications/notifications.service';
import {
  FullUser,
  FullUserUpdateRequest,
  GeneralProfileUpdateRequest,
  Individual,
  LocationObject,
  Viewport,
} from 'src/app/swagger-api';
import { DefaultService as RestApiService } from 'src/app/swagger-api/api/default.service';
import { MaybeUndefined } from 'src/app/utils';
import { AuthService } from '../../../../auth/auth.service';
import { ManageRolesContent, PrepareSaveInfo, ProfileFormValue } from '../../../../interfaces/profile.interface';
import { ProfileService } from '../../profile.service';
import { FormContentService } from './form-content.service';
import {
  aliasMetadata,
  organizationNameMetadata,
  profileImageMetadata,
  verifiedEmailMetadata,
} from './profile-forms.metadata';

@Injectable()
export class EditProfileService {
  private apiService = inject(RestApiService);
  private authService = inject(AuthService);
  private profileService = inject(ProfileService);
  private formContentService = inject(FormContentService);
  private sessionService = inject(SessionService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  public updateUserInfo(formsValue: ProfileFormValue[], userType: string): void {
    if (formsValue[0].formValue['verified_email']) {
      const body: FullUserUpdateRequest = this.prepareInfoForSave({
        formsValue,
        userType,
      });

      formsValue = this.processCategories(formsValue);

      this.apiService
        .restUserPut(body, this.sessionService.userId)
        .pipe(catchError(() => EMPTY))
        .subscribe((user: FullUser) => {
          this.sessionService.user = user;
          promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_PROFILE_VIEW));
        });
    } else {
      this.showModal('Error', `Can't save your info`);
    }
  }

  private processCategories(formsValue: ProfileFormValue[]): ProfileFormValue[] {
    formsValue.forEach((value: ProfileFormValue) => {
      if ('diseases' in value.formValue) {
        value.formValue['disease_ids'] = value.formValue['diseases'];
        delete value.formValue['diseases'];
      }

      if ('treatment_of_diseases' in value.formValue) {
        value.formValue['disease_ids'] = value.formValue['treatment_of_diseases'];
        delete value.formValue['treatment_of_diseases'];
      }

      if ('specialty' in value.formValue) {
        value.formValue['specialty_ids'] = value.formValue['specialty'];
        delete value.formValue['specialty'];
      }
    });
    return formsValue;
  }

  public manageRoles(content: ManageRolesContent): void {
    const incomingRole: RoleInfo = this.getSelectedRole(content.roles);
    const currentRole: RoleInfo = this.getSelectedRole(this.profileService.userRolesInfo);

    if (this.needToBeUpdated(currentRole, incomingRole)) {
      this.updateFormContent(content);
    }

    this.removeRoles(content);
    this.addNewRole(content);
  }

  private removeRoles(content: ManageRolesContent): void {
    const rolesToDelete: Set<string> = new Set();
    const unselectedRoles: Set<string> = new Set();

    content.roles.forEach((role: RoleInfo) => {
      if (!role.isSelected && role.isSaved) {
        // If a role is not selected and was saved (stored in the db), add it to the roles to delete
        rolesToDelete.add(role.value);
      }

      if (!role.isSelected) {
        // If a role is unselected, add it to the selected roles
        unselectedRoles.add(role.value);
      }
    });

    content.formsContent.forEach((formContent: FormConstructorContent) => {
      if (formContent.formAttribute == undefined || formContent.formAttribute.type == undefined) return;

      const roleType: string = formContent.formAttribute.type;

      if (unselectedRoles.has(roleType)) {
        // If a role is not selected anymore (but it was never stored in BE), remove it from the form
        rolesToDelete.add(roleType);
      }
    });

    rolesToDelete.forEach((roleType: string) => {
      // Find index of role to remove
      const index: number = this.profileService.userRolesInfo.findIndex((role: RoleInfo) => role.value === roleType);

      // If a role is found, remove it from the form
      if (index >= 0) {
        this.formContentService.removeUserRole(roleType);
      }
    });
  }

  public deleteProfile(userId: string): void {
    this.apiService
      .restUserDelete(userId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.authService.clientSideLogout();
      });
  }

  private getFormValue(formsValue: ProfileFormValue[], type: string): ProfileFormValue {
    return formsValue.filter((form: ProfileFormValue) => form.formType === type)[0];
  }

  /**
   * @description Creates profile property object by incoming form values.
   *              Sets unknown location value in case no location.
   * @param { ProfileFormValue[] } values
   * @returns { GeneralProfileUpdateRequest }
   */
  private buildGeneralProfileUpdateRequest(values: ProfileFormValue[]): GeneralProfileUpdateRequest {
    const rawFormData: object = Object.assign(
      {},
      ...values.map(({ formValue }: ProfileFormValue) => formValue),
    ) as object;

    const profile: GeneralProfileUpdateRequest = {
      email: rawFormData['email'] as string,
      image_url: rawFormData['image_url'] as string,
      location: rawFormData['location'] as LocationObject,
      viewport: rawFormData['viewport'] as Viewport,
      about: rawFormData['about'] as string,
    };

    if (!profile.location) {
      return { ...profile, location: unknownLocation() };
    }

    return profile;
  }

  private separateSpecialFieldsFromProfileGeneralData(content: PrepareSaveInfo): void {
    const baseFormValue: object = content.formsValue[0].formValue;
    content.formsValue.splice(0, 1);

    const formKeys: string[] = Object.keys(baseFormValue);

    const isAdminSession = this.sessionService.isAdminSession();
    const profileGeneralKeys: string[] = formKeys.filter(
      (key: string) =>
        key !== verifiedEmailMetadata.name &&
        key !== aliasMetadata.name &&
        key !== organizationNameMetadata.name &&
        !(key === profileImageMetadata.name && isAdminSession),
    );

    const generalData: object = {};
    profileGeneralKeys.forEach((key: string) => {
      generalData[key] = baseFormValue[key] as object;
    });

    content.formsValue.push({
      formType: ProfileFormTypes.PROFILE,
      formValue: generalData,
    });

    content.formsValue.push({
      formType: ProfileFormTypes.BASE,
      formValue: { email: baseFormValue[verifiedEmailMetadata.name] as string },
    });

    if (isAdminSession) {
      content.formsValue.push({
        formType: ProfileFormTypes.INDIVIDUAL,
        formValue: { alias: baseFormValue[aliasMetadata.name] as string },
      });
    } else if (this.isAliasFieldDefinedInForm(formKeys)) {
      const individualForm: MaybeUndefined<ProfileFormValue> = content.formsValue.find(
        (form: ProfileFormValue) => form.formType === ProfileFormTypes.INDIVIDUAL,
      );

      if (individualForm) {
        individualForm.formValue[aliasMetadata.name] = baseFormValue[aliasMetadata.name] as ProfileFormValue;
      }
    } else if (this.isOrganizationNameFieldDefinedInForm(formKeys)) {
      const organizationForm: MaybeUndefined<ProfileFormValue> = content.formsValue.find(
        (form: ProfileFormValue) => form.formType === ProfileFormTypes.ORGANIZATION,
      );

      if (organizationForm) {
        organizationForm.formValue[organizationNameMetadata.name] = baseFormValue[
          organizationNameMetadata.name
        ] as ProfileFormValue;
      }
    }
  }

  private isAliasFieldDefinedInForm(formKeys: string[]): boolean {
    return formKeys.some((key: string) => key === aliasMetadata.name);
  }

  private isOrganizationNameFieldDefinedInForm(formKeys: string[]): boolean {
    return formKeys.some((key: string) => key === organizationNameMetadata.name);
  }

  private prepareInfoForSave(content: PrepareSaveInfo): FullUserUpdateRequest {
    this.separateSpecialFieldsFromProfileGeneralData(content);

    const profileValues: ProfileFormValue[] = content.formsValue.filter(
      (form: ProfileFormValue) => form.formType === ProfileFormTypes.PROFILE || form.formType === ProfileFormTypes.BASE,
    );

    const request = this.buildFullUserUpdateRequest(content, profileValues);

    if (request.individual) {
      this.deleteEmptyIndividualValues(request.individual);
    }

    return request;
  }

  private buildFullUserUpdateRequest(
    content: PrepareSaveInfo,
    profileValues: ProfileFormValue[],
  ): FullUserUpdateRequest {
    const user: FullUserUpdateRequest = {
      profile: this.buildGeneralProfileUpdateRequest(profileValues),
    };
    if (this.isUserTypeDefined(content.userType)) {
      user[content.userType] = this.getFormValue(content.formsValue, content.userType).formValue;
    }
    this.prepareRolesModels(content, user);
    return user;
  }

  private prepareRolesModels(content: PrepareSaveInfo, user: FullUserUpdateRequest): void {
    if (this.profileService.userRolesInfo && this.profileService.userRolesInfo.length > 0) {
      this.profileService.userRolesInfo.forEach((roleInfo: RoleInfo) => {
        user[roleInfo.value] = this.getFormValue(content.formsValue, roleInfo.value).formValue;
      });
    }
  }

  private addNewRole(content: ManageRolesContent): void {
    if (!content.formsContent.length) {
      this.formContentService.getRoleFormContent();
    }

    const newRoles: RoleInfo[] = content.roles.filter((role: RoleInfo) => role.isSelected && !role.isSaved);

    newRoles.forEach((newRole: RoleInfo) => {
      const index: number = this.profileService.userRolesInfo.findIndex(
        (role: RoleInfo) => role.value === newRole.value,
      );

      if (index < 0) {
        this.formContentService.addUserRole(newRole.value);
      }
    });
  }

  private updateFormContent(content: ManageRolesContent): void {
    if (this.profileService.userRolesInfo.length === 0) {
      content.formsContent = content.formsContent.filter(
        (formContent: FormConstructorContent) =>
          formContent.formAttribute.type !== ProfileFormTypes.INDIVIDUAL &&
          formContent.formAttribute.type !== ProfileFormTypes.PROFILE,
      );
    }

    this.formContentService.notifyFormChangesSubscribers(content.formsContent);
  }

  private getSelectedRole(roleList: RoleInfo[]): RoleInfo {
    return roleList.find((role: RoleInfo) => !role.isSaved && role.isSelected);
  }

  private needToBeUpdated(current: RoleInfo, incoming: RoleInfo): boolean {
    return current && ((incoming && current.value !== incoming.value) || !incoming);
  }

  private showModal(title: string, description: string): void {
    this.notificationService.dialogDefault({ title, description, buttons: [] });
  }

  private isUserTypeDefined(type: string): type is DefinedUserType {
    return definedUserTypes.includes(type);
  }

  private deleteEmptyIndividualValues(individual: Individual): void {
    for (const key in individual) {
      if (individual[key] === '') {
        delete individual[key];
      }
    }
  }
}
